import React from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { object, string } from 'yup'

import Logo from 'assets/images/logo.png'
import { Text } from 'components/Text'
import {
  ResetPasswordLink,
  ResetPasswordText,
  SignInLoadingContainer,
  SignInModalButton,
  SignInModalContainer,
  SignInModalContent,
  SignInModalFooter,
  SignInModalForm,
  SignInModalHeader,
  SignInModalHeaderLogo,
} from './SignInModal.styled'
import { useAppDispatch, useAppState } from 'store'
import { authActions } from 'store/slices/auth'
import { unwrapResult } from '@reduxjs/toolkit'
import { SignInModalProps } from './SignInModal.props'
import { Input } from 'components/Input'
import { useEncrypt } from 'hooks/useEncrypt'
import Spinner from 'components/Spinner'
import { SignInResponse } from 'services/auth/auth.types'
import { useHistory, useParams } from 'react-router'
import { validateEmail } from '../../pages/SignUp/SignUp.page';

type Form = {
  email: string
  password: string
}

const schema = object().shape({
  email: string().required().email(),
  password: string().required(),
})

const SignInModal = ({ onModalClose }: SignInModalProps) => {
  const { push } = useHistory()
  const { authStatus } = useAppState(state => state.auth)
  const dispatch = useAppDispatch()
  const { encryptPhrase } = useEncrypt()

  const {
    handleSubmit,
    register,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<Form>({
    resolver: yupResolver(schema),
  })

  const onSubmit = handleSubmit(data => {
    const encryption = encryptPhrase(data.password)
    data.password = encryption
    dispatch(authActions.signIn(data))
      .then(res => {
        unwrapResult(res)
        const { user_data } = res.payload as SignInResponse
        if (user_data) {
          if (user_data.location_id && user_data.location_id.length > 0) {
            push(`/locations/${user_data.location_id[0]}`)
          } else {
            push('/profile')
          }
          onModalClose();
        }
      })
  })

  const resetPassword = () => {
    clearErrors();
    const { email } = getValues();
    if (email === '') {
      setError("email", {
        type: "required", message: "email is a required field"
      });
    }
    else if (!validateEmail(email)) {
      setError("email", {
        type: "regex", message: "email must be a valid email"
      });
    }
    else {
      dispatch(authActions.resetPassword({
        userId: '0',
        email
      }));
    }
  }

  return (
    <SignInModalContainer
      title=""
      showClose={false}
      onClose={onModalClose}
      containerStyle={{
        boxShadow: '0px 0px 15px 0px rgba(241, 241, 241, 1)',
        paddingTop: 68,
        paddingLeft: 68,
        paddingRight: 68,
        paddingBottom: 40,
        maxWidth: 540,
        borderRadius: 55,
        minHeight: 340,
      }}>
      {authStatus === 'loading' ? (
        <SignInLoadingContainer>
          <Spinner />
        </SignInLoadingContainer>
      ) : (
        <SignInModalContent>
          <SignInModalHeader>
            <SignInModalHeaderLogo src={Logo} alt="Acespace Courts" />
            <Text
              text="Welcome back! Please sign in to continue."
              preset="h6"
            />
          </SignInModalHeader>
          <SignInModalForm style={{ padding: '10px' }}>
            <Input
              type="email"
              label="Email"
              error={errors.email && errors.email.message}
              {...register('email')}
            />
            <Input
              type="password"
              {...register('password')}
              label="Password"
              error={errors.password && errors.password.message}
            />
          </SignInModalForm>
          <SignInModalFooter>
            <SignInModalButton
              size="medium"
              text="Cancel"
              variant="secondary"
              preset="filled"
              onClick={onModalClose}
            />
            <SignInModalButton
              size="medium"
              text="Login"
              variant="primary"
              preset="filled"
              onClick={onSubmit}
            />
          </SignInModalFooter>
          <ResetPasswordText>
            Forgot your password?
            <ResetPasswordLink onClick={resetPassword}>Reset it here</ResetPasswordLink>
          </ResetPasswordText>
        </SignInModalContent>
      )}
    </SignInModalContainer>
  )
}

export default SignInModal
